import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowsClockwise } from "@phosphor-icons/react";

// Health indicator + on-demand syncer for the ScaledEasy → CVC session-title feed.
//
// Indicator dot semantics (matches backend get_sync_state()):
//   green  = last sync attempt succeeded AND last success < 3 days ago
//   yellow = last sync attempt FAILED but a prior success exists within 3 days
//   red    = no success ever, OR last success >= 3 days ago (data may be stale)
//   grey   = state unknown (never called sync-state, or admin just logged in)
//
// The daily background loop on the backend runs independently regardless of
// whether this button is clicked. This is purely for on-demand re-sync.
export function ApproachSyncButton() {
  const [syncing, setSyncing] = useState(false);
  const [state, setState] = useState(null);  // { status, last_attempt_ok, days_since_success, last_error, ... }

  const refreshState = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/approach/sync-state");
      setState(data);
    } catch (e) {
      console.warn("Approach sync-state unavailable; indicator shows grey.", e?.message);
    }
  }, []);

  useEffect(() => { refreshState(); }, [refreshState]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/approach/sync");
      if (data.ok) {
        const bits = [];
        if (data.updated)   bits.push(`${data.updated} updated`);
        if (data.inserted)  bits.push(`${data.inserted} inserted`);
        if (data.unchanged) bits.push(`${data.unchanged} unchanged`);
        toast.success(bits.length ? `Synced: ${bits.join(", ")}.` : "Synced. No changes.");
      } else {
        toast.error(`Sync failed: ${data.error || "unknown reason"}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync request failed");
    } finally {
      setSyncing(false);
      refreshState();
    }
  };

  const status = state?.status || "grey";
  const dotClass = {
    green:  "bg-emerald-500",
    yellow: "bg-amber-400",
    red:    "bg-red-500",
    grey:   "bg-cvc-ink-soft/40",
  }[status];

  const tooltip = (() => {
    if (!state || !state.last_attempt_at) return "Sync status unknown. Click to sync now. (Also runs automatically every 24 hours.)";
    const dsr = state.days_since_success;
    const daysStr = dsr === null ? "never" : dsr < 1 ? "less than a day ago" : dsr < 2 ? "yesterday" : `${Math.floor(dsr)} days ago`;
    if (status === "green")  return `Last sync succeeded ${daysStr}. Click to re-sync now. (Also runs automatically every 24 hours.)`;
    if (status === "yellow") return `Last sync FAILED (${state.last_error || "reason unknown"}), but prior success was ${daysStr}. Click to retry. (Also runs automatically every 24 hours.)`;
    if (status === "red")    return `Sync is stale (last success: ${daysStr}). Titles may be out of date. Click to retry. (Also runs automatically every 24 hours.)`;
    return "Click to sync now. (Also runs automatically every 24 hours.)";
  })();

  return (
    <button
      data-testid="approach-sync-button"
      onClick={doSync}
      disabled={syncing}
      title={tooltip}
      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-body font-semibold border transition-colors bg-cvc-ink-soft/10 text-cvc-ink border-cvc-border hover:bg-cvc-ink-soft/20 disabled:opacity-60 disabled:cursor-wait"
    >
      <span
        data-testid={`approach-sync-indicator-${status}`}
        className={`inline-block w-2 h-2 rounded-full ${dotClass} ${syncing ? "animate-pulse" : ""}`}
      />
      <ArrowsClockwise size={13} weight="bold" className={syncing ? "animate-spin" : ""} />
      {syncing ? "Syncing…" : "Sync latest 100 sessions from ScaledEasy now"}
    </button>
  );
}
